import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import { describeError } from '../api/errors'
import type { Game, Room } from '../api/rooms'
import { auth, games, rooms } from '../api/rooms'
import { formatTime } from '../lib/format'
import { navigate } from '../lib/router'
import { pushToast } from '../lib/toast'

/**
 * Публичная витрина: список готовых озвучек и вход в комнату.
 * Это первый экран для всех, кроме автора (docs/ROOMS.md).
 */
export function GamesPage(): JSX.Element {
  const [list, setList] = useState<Game[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authRequired, setAuthRequired] = useState(false)
  const [myRooms, setMyRooms] = useState<Room[]>([])
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    try {
      const [catalogue, state] = await Promise.all([games.list(), auth.me()])
      setList(catalogue)
      setIsAdmin(state.isAdmin)
      setAuthRequired(state.authRequired)
      setError(null)
      if (state.isAdmin) {
        try {
          setMyRooms(await rooms.list())
        } catch {
          setMyRooms([])
        }
      }
    } catch (err) {
      setError(describeError(err))
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const enterByCode = (): void => {
    const clean = code.trim().toUpperCase()
    if (!clean) return
    navigate({ name: 'room', code: clean })
  }

  const createRoom = async (): Promise<void> => {
    setBusy(true)
    try {
      const view = await rooms.create('Комната')
      navigate({ name: 'room', code: view.room.code })
    } catch (err) {
      pushToast('error', describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page games-page">
      <section className="hero">
        <h1>Озвучки</h1>
        <p className="faint">
          Выберите сцену и озвучьте её по ролям — в одиночку или с друзьями по ссылке.
        </p>
        <div className="hero-actions">
          <div className="join-box">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') enterByCode()
              }}
              placeholder="Код комнаты"
              maxLength={8}
              aria-label="Код комнаты"
              className="code-input"
            />
            <button type="button" onClick={enterByCode} disabled={!code.trim()}>
              Войти
            </button>
          </div>
          {isAdmin ? (
            <>
              <button type="button" className="primary" onClick={() => void createRoom()} disabled={busy}>
                Создать комнату
              </button>
              <button type="button" className="ghost" onClick={() => navigate({ name: 'projects' })}>
                Мои проекты
              </button>
            </>
          ) : authRequired ? (
            <button type="button" className="ghost" onClick={() => navigate({ name: 'login' })}>
              Вход для автора
            </button>
          ) : null}
        </div>
      </section>

      {error ? <p className="error-text">{error}</p> : null}

      {isAdmin && myRooms.length > 0 ? (
        <section className="block">
          <h2>Мои комнаты</h2>
          <ul className="room-list">
            {myRooms.map((room) => (
              <li key={room.id}>
                <button type="button" className="link" onClick={() => navigate({ name: 'room', code: room.code })}>
                  <span className="mono">{room.code}</span> — {room.title}
                </button>
                <span className="faint">
                  {room.state === 'lobby' ? 'ожидание' : room.state === 'playing' ? 'идёт игра' : 'результат'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="block">
        <h2>Что можно озвучить</h2>
        {list === null ? (
          <p className="faint">Загружаю…</p>
        ) : list.length === 0 ? (
          <p className="faint">
            Пока пусто.{' '}
            {isAdmin
              ? 'Откройте проект в редакторе и нажмите «Опубликовать как озвучку».'
              : 'Автор ещё не опубликовал ни одной сцены.'}
          </p>
        ) : (
          <ul className="game-grid">
            {list.map((game) => (
              <li key={game.id} className="game-card">
                <div className="game-card-body">
                  <h3>{game.title}</h3>
                  {game.description ? <p className="faint">{game.description}</p> : null}
                  <p className="game-meta">
                    {formatTime(game.duration)} · ролей {game.rolesCount} · реплик {game.linesCount}
                    {game.mode === 'replace' ? ' · полный дубляж' : ''}
                    {!game.published ? ' · черновик' : ''}
                  </p>
                </div>
                <div className="game-card-actions">
                  {isAdmin ? (
                    <button
                      type="button"
                      className="ghost danger"
                      onClick={() => {
                        if (!confirm(`Удалить озвучку «${game.title}»?`)) return
                        void games
                          .remove(game.id)
                          .then(reload)
                          .catch((err: unknown) => pushToast('error', describeError(err)))
                      }}
                    >
                      Удалить
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="primary"
                    onClick={() => {
                      if (isAdmin) void createRoomWith(game.id)
                      else pushToast('info', 'Попросите ведущего создать комнату и прислать ссылку')
                    }}
                  >
                    {isAdmin ? 'Играть в комнате' : 'Нужна комната'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )

  async function createRoomWith(gameId: string): Promise<void> {
    try {
      const view = await rooms.create('Комната')
      await rooms.setGame(view.room.id, gameId)
      navigate({ name: 'room', code: view.room.code })
    } catch (err) {
      pushToast('error', describeError(err))
    }
  }
}
